import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
// jsPDF, marked, and Tone are all dynamically imported at their point of use instead of
// statically here — each is only needed for a specific action (PDF export, audio playback),
// not on every page load, and together they were a disproportionate chunk of the eagerly-
// loaded main bundle.
import { loadTone } from './utils/lazyTone.js';
// import { Analytics } from '@vercel/analytics/react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { track } from '@vercel/analytics';
import OfflineBanner from './components/OfflineBanner.jsx';
import InstallAppBanner from './components/InstallAppBanner.jsx';
import './App.css';
import LandingPage from './components/LandingPage.jsx';
import PathwayPage from './pages/PathwayPage.jsx';
import EULAModal from './components/EULAModal.jsx';
import LoginModal from './components/LoginModal.jsx';
import CheckoutSuccess from './components/CheckoutSuccess.jsx';
import CheckoutCancel from './components/CheckoutCancel.jsx';
import PlanChooserModal from './components/PlanChooserModal.jsx';
import BillingPortalModal from './components/BillingPortalModal.jsx';
import EulaPage from './pages/EulaPage.jsx';
import StaticDocPage from './pages/StaticDocPage.jsx';
import AppHome from './pages/AppHome.jsx';
import DeckPage from './pages/DeckPage.jsx';
import FlashClinicalLandingPage from './pages/FlashClinicalLandingPage.jsx';
import PublicStudyPage from './pages/PublicStudyPage.jsx';
import AccountPage from './components/AccountPage.jsx';
import { ENV } from './config/env.js';
import { initFirebase, trackEvent, setAnalyticsUserProps, authApi, authApiIdempotent, onAuth, setUnauthorizedHandler, signInAnonymously, getAuthInstance, waitForUserAvailable, getDb, waitForDbAvailable, completeMagicLinkIfPresent } from './firebase/config.js';
import { trackPixels } from './analytics/pixels.js';
import { doc as fsDoc, getDoc } from 'firebase/firestore';
import { Events } from './firebase/events.js';
import { recordExamResult } from './firebase/progress.js';
import { FlashFonicProvider } from './contexts/FlashFonicContext.js'
import Modal from './components/ui/Modal.jsx';

// Route -> tab title, also fed to GA4 as page_title (see the page_view tracking effect below).
// Nothing set this per-route before, so every page reported the same title in Analytics.
function getPageTitle(pathname) {
    if (pathname === '/') return 'FlashFonic';
    if (pathname === '/app' || pathname === '/app/home') return 'Home — FlashFonic';
    if (pathname.startsWith('/app/deck/')) return 'Deck — FlashFonic';
    if (pathname === '/app/live') return 'Record — FlashFonic';
    if (pathname === '/app/upload') return 'Upload — FlashFonic';
    if (pathname === '/app/study') return 'Study — FlashFonic';
    if (pathname === '/app/exams') return 'Exams — FlashFonic';
    if (pathname === '/app/games') return 'Games — FlashFonic';
    if (pathname === '/app/notes') return 'Flash Notes — FlashFonic';
    if (pathname === '/app/folders') return 'Folders — FlashFonic';
    if (pathname === '/clinicals') return 'FlashClinical — FlashFonic';
    if (pathname.startsWith('/for/')) return 'FlashFonic';
    if (pathname.startsWith('/s/')) return 'Shared Study Set — FlashFonic';
    if (pathname === '/eula') return 'EULA — FlashFonic';
    if (pathname === '/terms') return 'Terms and Conditions — FlashFonic';
    if (pathname === '/privacy') return 'Privacy Policy — FlashFonic';
    if (pathname === '/account') return 'Account — FlashFonic';
    if (pathname === '/success') return 'Purchase Complete — FlashFonic';
    if (pathname === '/cancel') return 'Checkout Canceled — FlashFonic';
    return 'FlashFonic';
}

// These routes render nothing but <Navigate replace> to /app/home (see the Route defs
// below) — legacy URLs kept alive only so old bookmarks/links still land somewhere. React
// Router still commits a render with the old pathname before the redirect fires, so without
// this guard every hit on one of them logged a phantom pageview under the dead path (e.g.
// "/app/exams") in addition to the real one for /app/home right after — showing up as rows
// with a couple of stray hits in GA4's Pages report.
const DEAD_REDIRECT_PATHS = new Set(['/app', '/app/live', '/app/upload', '/app/study', '/app/exams', '/app/games', '/app/notes', '/app/folders']);

// Backend API base URL (configurable via Vite env)
const API_BASE_URL = ENV.apiBaseUrl;

const SIGNUP_STORAGE_KEY = 'ff:signupFunnelShown';
const SIGNUP_GENERATION_THRESHOLD = 2;
const TOTAL_GENERATIONS_KEY = 'ff:totalGenerations'; // Track total generations for anonymous users
const USAGE_STORAGE_KEY = 'flashfonic-usage';
const getUsagePeriodKey = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${now.getFullYear()}-${month}`;
};

// Helper to track total generations and check if signup popup should show
const trackGenerationAndCheckSignup = (increment = 1) => {
    try {
        const stored = localStorage.getItem(TOTAL_GENERATIONS_KEY);
        const currentTotal = Number(stored || 0);
        const newTotal = currentTotal + increment;
        localStorage.setItem(TOTAL_GENERATIONS_KEY, String(newTotal));
        
        const signupShown = localStorage.getItem(SIGNUP_STORAGE_KEY) === '1';
        const shouldShow = !signupShown && currentTotal < SIGNUP_GENERATION_THRESHOLD && newTotal >= SIGNUP_GENERATION_THRESHOLD;
        
        return { shouldShow, newTotal };
    } catch (_) {
        return { shouldShow: false, newTotal: 0 };
    }
};


// --- EULA and Privacy Policy Content (Embedded as String Constants) ---
// NOTE: The 'eulaContent' constant now holds the Terms and Conditions.
const eulaContent = `
# Terms and Conditions

**Last updated:** August 27, 2025

Please read these terms and conditions carefully before using Our Service.

## Interpretation and Definitions

### Interpretation
The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.

### Definitions
For the purposes of these Terms and Conditions:

* **Application** means the software program provided by the Company downloaded by You on any electronic device, named FLASHFONIC.
* **Application Store** means the digital distribution service operated and developed by Apple Inc. (Apple App Store) or Google Inc. (Google Play Store) in which the Application has been downloaded.
* **Affiliate** means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.
* **Account** means a unique account created for You to access our Service or parts of our Service.
* **Country** refers to: Vermont, United States
* **Company** (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to Trifecta Pro LLC, PO Box 265, Bennington VT 05201.
* **Content** refers to content such as text, images, or other information that can be posted, uploaded, linked to or otherwise made available by You, regardless of the form of that content.
* **Device** means any device that can access the Service such as a computer, a cellphone or a digital tablet.
* **Feedback** means feedback, innovations or suggestions sent by You regarding the attributes, performance or features of our Service.
* **Free Trial** refers to a limited period of time that may be free when purchasing a Subscription.
* **Promotions** refer to contests, sweepstakes or other promotions offered through the Service.
* **Service** refers to the Application or the Website or both.
* **Subscriptions** refer to the services or access to the Service offered on a subscription basis by the Company to You.
* **Terms and Conditions** (also referred as "Terms") mean these Terms and Conditions that form the entire agreement between You and the Company regarding the use of the Service.
* **Third-party Social Media Service** means any services or content (including data, information, products or services) provided by a third-party that may be displayed, included or made available by the Service.
* **Website** refers to FLASHFONIC, accessible from www.flashfonic.com
* **You** means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.

## Acknowledgment
These are the Terms and Conditions governing the use of this Service and the agreement that operates between You and the Company. These Terms and Conditions set out the rights and obligations of all users regarding the use of the Service.
Your access to and use of the Service is conditioned on Your acceptance of and compliance with these Terms and Conditions. These Terms and Conditions apply to all visitors, users and others who access or use the Service.
By accessing or using the Service You agree to be bound by these Terms and Conditions. If You disagree with any part of these Terms and Conditions then You may not access the Service.
You represent that you are over the age of 18. The Company does not permit those under 18 to use the Service.
Your access to and use of the Service is also conditioned on Your acceptance of and compliance with the Privacy Policy of the Company. Our Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your personal information when You use the Application or the Website and tells You about Your privacy rights and how the law protects You. Please read Our Privacy Policy carefully before using Our Service.

## Subscriptions

### Subscription period
The Service or some parts of the Service are available only with a paid Subscription. You will be billed in advance on a recurring and periodic basis (such as daily, weekly, monthly or annually), depending on the type of Subscription plan you select when purchasing the Subscription.
At the end of each period, Your Subscription will automatically renew under the exact same conditions unless You cancel it or the Company cancels it.

### Subscription cancellations
You may cancel Your Subscription renewal either through Your Account settings page or by contacting the Company. You will not receive a refund for the fees You already paid for Your current Subscription period and You will be able to access the Service until the end of Your current Subscription period.

### Billing
You shall provide the Company with accurate and complete billing information including full name, address, state, zip code, telephone number, and a valid payment method information.
Should automatic billing fail to occur for any reason, the Company will issue an electronic invoice indicating that you must proceed manually, within a certain deadline date, with the full payment corresponding to the billing period as indicated on the invoice.

### Fee Changes
The Company, in its sole discretion and at any time, may modify the Subscription fees. Any Subscription fee change will become effective at the end of the then-current Subscription period.
The Company will provide You with reasonable prior notice of any change in Subscription fees to give You an opportunity to terminate Your Subscription before such change becomes effective.
Your continued use of the Service after the Subscription fee change comes into effect constitutes Your agreement to pay the modified Subscription fee amount.

### Refunds
Except when required by law, paid Subscription fees are non-refundable.
Certain refund requests for Subscriptions may be considered by the Company on a case-by-case basis and granted at the sole discretion of the Company.

### Free Trial
The Company may, at its sole discretion, offer a Subscription with a Free Trial for a limited period of time.
You may be required to enter Your billing information in order to sign up for the Free Trial.
If You do enter Your billing information when signing up for a Free Trial, You will not be charged by the Company until the Free Trial has expired. On the last day of the Free Trial period, unless You canceled Your Subscription, You will be automatically charged the applicable Subscription fees for the type of Subscription You have selected.
At any time and without notice, the Company reserves the right to (i) modify the terms and conditions of the Free Trial offer, or (ii) cancel such Free Trial offer.

## Promotions
Any Promotions made available through the Service may be governed by rules that are separate from these Terms.
If You participate in any Promotions, please review the applicable rules as well as our Privacy policy. If the rules for a Promotion conflict with these Terms, the Promotion rules will apply.

## User Accounts
When You create an account with Us, You must provide Us information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of Your account on Our Service.
You are responsible for safeguarding the password that You use to access the Service and for any activities or actions under Your password, whether Your password is with Our Service or a Third-Party Social Media Service.
You agree not to disclose Your password to any third party. You must notify Us immediately upon becoming aware of any breach of security or unauthorized use of Your account.
You may not use as a username the name of another person or entity or that is not lawfully available for use, a name or trademark that is subject to any rights of another person or entity other than You without appropriate authorization, or a name that is otherwise offensive, vulgar or obscene.

## Content

### Your Right to Post Content
Our Service allows You to post Content. You are responsible for the Content that You post to the Service, including its legality, reliability, and appropriateness.
By posting Content to the Service, You grant Us the right and license to use, modify, publicly perform, publicly display, reproduce, and distribute such Content on and through the Service. You retain any and all of Your rights to any Content You submit, post or display on or through the Service and You are responsible for protecting those rights. You agree that this license includes the right for Us to make Your Content available to other users of the Service, who may also use Your Content subject to these Terms.
You represent and warrant that: (i) the Content is Yours (You own it) or You have the right to use it and grant Us the rights and license as provided in these Terms, and (ii) the posting of Your Content on or through the Service does not violate the privacy rights, publicity rights, copyrights, contract rights or any other rights of any person.

### Content Restrictions
The Company is not responsible for the content of the Service's users. You expressly understand and agree that You are solely responsible for the Content and for all activity that occurs under Your account, whether done so by You or any third person using Your account.
You may not transmit any Content that is unlawful, offensive, upsetting, intended to disgust, threatening, libelous, defamatory, obscene or otherwise objectionable. Examples of such objectionable Content include, but are not limited to, the following:

* Unlawful or promoting unlawful activity.
* Defamatory, discriminatory, or mean-spirited content, including references or commentary about religion, race, sexual orientation, gender, national/ethnic origin, or other targeted groups.
* Spam, machine – or randomly – generated, constituting unauthorized or unsolicited advertising, chain letters, any other form of unauthorized solicitation, or any form of lottery or gambling.
* Containing or installing any viruses, worms, malware, trojan horses, or other content that is designed or intended to disrupt, damage, or limit the functioning of any software, hardware or telecommunications equipment or to damage or obtain unauthorized access to any data or other information of a third person.
* Infringing on any proprietary rights of any party, including patent, trademark, trade secret, copyright, right of publicity or other rights.
* Impersonating any person or entity including the Company and its employees or representatives.
* Violating the privacy of any third person.
* False information and features.

The Company reserves the right, but not the obligation, to, in its sole discretion, determine whether or not any Content is appropriate and complies with these Terms, refuse or remove this Content. The Company further reserves the right to make formatting and edits and change the manner of any Content. The Company can also limit or revoke the use of the Service if You post such objectionable Content. As the Company cannot control all content posted by users and/or third parties on the Service, you agree to use the Service at your own risk. You understand that by using the Service You may be exposed to content that You may find offensive, indecent, incorrect or objectionable, and You agree that under no circumstances will the Company be liable in any way for any content, including any errors or omissions in any content, or any loss or damage of any kind incurred as a result of your use of any content.

### Content Backups
Although regular backups of Content are performed, the Company does not guarantee there will be no loss or corruption of data.
Corrupt or invalid backup points may be caused by, without limitation, Content that is corrupted prior to being backed up or that changes during the time a backup is performed.
The Company will provide support and attempt to troubleshoot any known or discovered issues that may affect the backups of Content. But You acknowledge that the Company has no liability related to the integrity of Content or the failure to successfully restore Content to a usable state.
You agree to maintain a complete and accurate copy of any Content in a location independent of the Service.

## Copyright Policy

### Intellectual Property Infringement
We respect the intellectual property rights of others. It is Our policy to respond to any claim that Content posted on the Service infringes a copyright or other intellectual property infringement of any person.
If You are a copyright owner, or authorized on behalf of one, and You believe that the copyrighted work has been copied in a way that constitutes copyright infringement that is taking place through the Service, You must submit Your notice in writing to the attention of our copyright agent via email at dmca@flashfonic.com and include in Your notice a detailed description of the alleged infringement.
You may be held accountable for damages (including costs and attorneys' fees) for misrepresenting that any Content is infringing Your copyright.

### User-Uploaded Study Materials
When You upload a document (PDF, PowerPoint, Word, or similar file) to generate flashcards or exam questions, the Service may store that document itself — not only text extracted from it — so You can reopen it later and jump back to the exact page a flashcard or question was generated from. By uploading any such document, You represent and warrant that You own it or otherwise have the right to upload, store, and use it as described here, and You assume sole responsibility for any copyrighted, trademarked, or otherwise legally protected material it contains. The Company does not review uploaded documents for copyright status before storing them and disclaims all liability for infringing material a User uploads; copyright owners may use the DMCA process below to report it. Uploaded documents are retained according to Your plan (shorter windows on lower tiers, longer or indefinite retention on higher tiers) and may be deleted after that window, upon account or subscription cancellation, or in response to a valid takedown request. Photos taken of existing printed or displayed material (e.g., a textbook page) used only to extract text for flashcard generation are not stored as a document under this section.

### DMCA Notice and DMCA Procedure for Copyright Infringement Claims
You may submit a notification pursuant to the Digital Millennium Copyright Act (DMCA) by providing our Copyright Agent with the following information in writing (see 17 U.S.C 512(c)(3) for further detail):

* An electronic or physical signature of the person authorized to act on behalf of the owner of the copyright's interest.
* A description of the copyrighted work that You claim has been infringed, including the URL (i.e., web page address) of the location where the copyrighted work exists or a copy of the copyrighted work.
* Identification of the URL or other specific location on the Service where the material that You claim is infringing is located.
* Your address, telephone number, and email address.
* A statement by You that You have a good faith belief that the disputed use is not authorized by the copyright owner, its agent, or the law.
* A statement by You, made under penalty of perjury, that the above information in Your notice is accurate and that You are the copyright owner or authorized to act on the copyright owner's behalf.

You can contact our copyright agent via email at dmca@flashfonic.com. Upon receipt of a notification, the Company will take whatever action, in its sole discretion, it deems appropriate, including removal of the challenged content from the Service.

## Intellectual Property
The Service and its original content (excluding Content provided by You or other users), features and functionality are and will remain the exclusive property of the Company and its licensors.
The Service is protected by copyright, trademark, and other laws of both the Country and foreign countries.
Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of the Company.

## Your Feedback to Us
You assign all rights, title and interest in any Feedback You provide the Company. If for any reason such assignment is ineffective, You agree to grant the Company a non-exclusive, perpetual, irrevocable, royalty free, worldwide right and license to use, reproduce, disclose, sub-license, distribute, modify and exploit such Feedback without restriction.

## Links to Other Websites
Our Service may contain links to third-party web sites or services that are not owned or controlled by the Company.
The Company has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third party web sites or services. You further acknowledge and agree that the Company shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods or services available on or through any such web sites or services.
We strongly advise You to read the terms and conditions and privacy policies of any third-party web sites or services that You visit.

## Termination
We may terminate or suspend Your Account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if You breach these Terms and Conditions.
Upon termination, Your right to use the Service will cease immediately. If You wish to terminate Your Account, You may simply discontinue using the Service.

## Limitation of Liability
Notwithstanding any damages that You might incur, the entire liability of the Company and any of its suppliers under any provision of this Terms and Your exclusive remedy for all of the foregoing shall be limited to the amount actually paid by You through the Service or 100 USD if You haven't purchased anything through the Service.
To the maximum extent permitted by applicable law, in no event shall the Company or its suppliers be liable for any special, incidental, indirect, or consequential damages whatsoever (including, but not limited to, damages for loss of profits, loss of data or other information, for business interruption, for personal injury, loss of privacy arising out of or in any way related to the use of or inability to use the Service, third-party software and/or third-party hardware used with the Service, or otherwise in connection with any provision of this Terms), even if the Company or any supplier has been advised of the possibility of such damages and even if the remedy fails of its essential purpose.
Some states do not allow the exclusion of implied warranties or limitation of liability for incidental or consequential damages, which means that some of the above limitations may not apply. In these states, each party's liability will be limited to the greatest extent permitted by law.

## "AS IS" and "AS AVAILABLE" Disclaimer
The Service is provided to You "AS IS" and "AS AVAILABLE" and with all faults and defects without warranty of any kind. To the maximum extent permitted under applicable law, the Company, on its own behalf and on behalf of its Affiliates and its and their respective licensors and service providers, expressly disclaims all warranties, whether express, implied, statutory or otherwise, with respect to the Service, including all implied warranties of merchantability, fitness for a particular purpose, title and non-infringement, and warranties that may arise out of course of dealing, course of performance, usage or trade practice. Without limitation to the foregoing, the Company provides no warranty or undertaking, and makes no representation of any kind that the Service will meet Your requirements, achieve any intended results, be compatible or work with any other software, applications, systems or services, operate without interruption, meet any performance or reliability standards or be error free or that any errors or defects can or will be corrected.
Without limiting the foregoing, neither the Company nor any of the company's provider makes any representation or warranty of any kind, express or implied: (i) as to the operation or availability of the Service, or the information, content, and materials or products included thereon; (ii) that the Service will be uninterrupted or error-free; (iii) as to the accuracy, reliability, or currency of any information or content provided through the Service; or (iv) that the Service, its servers, the content, or e-mails sent from or on behalf of the Company are free of viruses, scripts, trojan horses, worms, malware, timebombs or other harmful components.
Some jurisdictions do not allow the exclusion of certain types of warranties or limitations on applicable statutory rights of a consumer, so some or all of the above exclusions and limitations may not apply to You. But in such a case the exclusions and limitations set forth in this section shall be applied to the greatest extent enforceable under applicable law.

## Governing Law
The laws of the Country, excluding its conflicts of law rules, shall govern this Terms and Your use of the Service. Your use of the Application may also be subject to other local, state, national, or international laws.

## Disputes Resolution
If You have any concern or dispute about the Service, You agree to first try to resolve the dispute informally by contacting the Company.

### For European Union (EU) Users
If You are a European Union consumer, you will benefit from any mandatory provisions of the law of the country in which you are resident.

### United States Federal Government End Use Provisions
If You are a U.S. federal government end user, our Service is a "Commercial Item" as that term is defined at 48 C.F.R. §2.101.

### United States Legal Compliance
You represent and warrant that (i) You are not located in a country that is subject to the United States government embargo, or that has been designated by the United States government as a "terrorist supporting" country, and (ii) You are not listed on any United States government list of prohibited or restricted parties.

## Severability and Waiver

### Severability
If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.

### Waiver
Except as provided herein, the failure to exercise a right or to require performance of an obligation under these Terms shall not affect a party's ability to exercise such right or require such performance at any time thereafter nor shall the waiver of a breach constitute a waiver of any subsequent breach.

## Translation Interpretation
These Terms and Conditions may have been translated if We have made them available to You on our Service. You agree that the original English text shall prevail in the case of a dispute.

## Changes to These Terms and Conditions
We reserve the right, at Our sole discretion, to modify or replace these Terms at any time. If a revision is material We will make reasonable efforts to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at Our sole discretion.
By continuing to access or use Our Service after those revisions become effective, You agree to be bound by the revised terms. If You do not agree to the new terms, in whole or in part, please stop using the website and the Service.

## Contact Us
If you have any questions about these Terms and Conditions, You can contact us:

* By email: feedbackflashfonic@gmail.com
`;


// ... (eulaContent constant should be above this)

const privacyPolicyContent = `
# Privacy Policy

**Last updated:** August 27, 2025

This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.

## Interpretation and Definitions

### Interpretation
The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.

### Definitions
For the purposes of this Privacy Policy:

* **Account** means a unique account created for You to access our Service or parts of our Service.
* **Affiliate** means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.
* **Application** refers to FLASHFONIC, the software program provided by the Company.
* **Business**, for the purpose of CCPA/CPRA, refers to the Company as the legal entity that collects Consumers' personal information and determines the purposes and means of the processing of Consumers' personal information...
* **CCPA and/or CPRA** refers to the California Consumer Privacy Act (the "CCPA") as amended by the California Privacy Rights Act of 2020 (the "CPRA").
* **Company** (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to Trifecta Pro LLC, PO Box 265, Bennington VT 05201. For the purpose of the GDPR, the Company is the Data Controller.
* **Consumer**, for the purpose of the CCPA/CPRA, means a natural person who is a California resident...
* **Cookies** are small files that are placed on Your computer, mobile device or any other device by a website...
* **Country** refers to: Vermont, United States
* **Data Controller**, for the purposes of the GDPR, refers to the Company as the legal person which alone or jointly with others determines the purposes and means of the processing of Personal Data.
* **Device** means any device that can access the Service such as a computer, a cellphone or a digital tablet.
* **GDPR** refers to EU General Data Protection Regulation.
* **Personal Data** is any information that relates to an identified or identifiable individual.
* **Service** refers to the Application or the Website or both.
* **Service Provider** means any natural or legal person who processes the data on behalf of the Company...
* **Usage Data** refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself.
* **Website** refers to FLASHFONIC, accessible from www.flashfonic.com
* **You** means the individual accessing or using the Service... Under GDPR, You can be referred to as the Data Subject or as the User.

## Collecting and Using Your Personal Data

### Types of Data Collected

#### Personal Data
While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:
* Email address
* First name and last name
* Address, State, Province, ZIP/Postal code, City
* Usage Data

#### Usage Data
Usage Data is collected automatically when using the Service. It may include information such as Your Device's IP address, browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, and other diagnostic data.

### Tracking Technologies and Cookies
We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information. These technologies may include:

* **Cookies or Browser Cookies:** A small file placed on Your Device. You can instruct Your browser to refuse all Cookies.
* **Web Beacons:** Small electronic files known as web beacons that permit the Company to count users who have visited pages or opened an email.

We use both Session and Persistent Cookies for purposes such as:
* **Necessary / Essential Cookies (Session)**: To authenticate users and prevent fraudulent use.
* **Cookies Policy / Notice Acceptance Cookies (Persistent)**: To identify if users have accepted the use of cookies.
* **Functionality Cookies (Persistent)**: To remember choices You make, such as login details or language preference.
* **Tracking and Performance Cookies (Persistent)**: To track information about traffic and how users use the Website.

### Use of Your Personal Data
The Company may use Personal Data for the following purposes:
* To provide and maintain our Service.
* To manage Your Account.
* For the performance of a contract.
* To contact You with updates or informative communications.
* To provide You with news, special offers, and general information.
* To manage Your requests.
* To deliver targeted advertising to You.
* For business transfers, such as a merger or acquisition.
* For other purposes, such as data analysis and improving our Service.

We may share Your personal information in the following situations:
* With Service Providers to monitor and analyze the use of our Service.
* For business transfers.
* With Our affiliates.
* With business partners.
* With other users when you interact in public areas.
* With Your consent.

### Retention of Your Personal Data
The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy.

### Transfer of Your Personal Data
Your information, including Personal Data, is processed at the Company's operating offices. Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement to that transfer.

### Delete Your Personal Data
You have the right to delete or request that We assist in deleting the Personal Data that We have collected about You. You may update, amend, or delete Your information at any time by signing in to Your Account.

### Disclosure of Your Personal Data
Your Personal Data may be disclosed for Business Transactions, in response to Law Enforcement requests, or to comply with other legal requirements.

### Security of Your Personal Data
The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure.

## Detailed Information on the Processing of Your Personal Data
The Service Providers We use may have access to Your Personal Data.

* **Analytics:** We may use third-party Service providers like Google Analytics and Firebase.
* **Email Marketing:** We may use Email Marketing Service Providers like Mailchimp.
* **Payments:** We may use third-party payment processors like Stripe, Apple Store In-App Payments, and Google Play In-App Payments.
* **Behavioral Remarketing:** The Company uses remarketing services (e.g., Google Ads, Bing Ads, Twitter, Facebook) to advertise to You after You visit our Service.

## GDPR Privacy
### Legal Basis for Processing Personal Data under GDPR
We may process Personal Data under conditions such as Consent, Performance of a contract, Legal obligations, Vital interests, Public interests, and Legitimate interests.

### Your Rights under the GDPR
If You are within the EU, you have the right to:
* Request access to Your Personal Data.
* Request correction of Your Personal Data.
* Object to processing of Your Personal Data.
* Request erasure of Your Personal Data.
* Request the transfer of Your Personal Data.
* Withdraw Your consent.

### Exercising of Your GDPR Data Protection Rights
You may exercise Your rights by contacting Us. You also have the right to complain to a Data Protection Authority.

## CCPA/CPRA Privacy Notice (California Privacy Rights)
This section supplements the information in Our Privacy Policy and applies solely to California residents.

### Categories of Personal Information Collected
We may have collected the following categories of personal information within the last twelve (12) months:
* **Category A:** Identifiers. (Collected: Yes)
* **Category B:** Personal information categories listed in the California Customer Records statute. (Collected: Yes)
* **Category C:** Protected classification characteristics under California or federal law. (Collected: No)
* **Category D:** Commercial information. (Collected: Yes)
* **Category E:** Biometric information. (Collected: No)
* **Category F:** Internet or other similar network activity. (Collected: Yes)
* **Category G:** Geolocation data. (Collected: No)
* **Category H:** Sensory data. (Collected: No)
* **Category I:** Professional or employment-related information. (Collected: No)
* **Category J:** Non-public education information. (Collected: No)
* **Category K:** Inferences drawn from other personal information. (Collected: No)
* **Category L:** Sensitive personal information. (Collected: Yes)

### Your Rights under the CCPA/CPRA
If You are a resident of California, You have the right to notice, the right to know/access, the right to say no to the sale or sharing of Personal Data (opt-out), the right to correct, the right to limit use of sensitive Personal Data, the right to delete, and the right not to be discriminated against. To exercise any of these rights, please contact Us.

### Do Not Sell My Personal Information
You have the right to opt-out of the sale of Your personal information. To exercise Your right to opt-out, please contact Us or follow the instructions on our Service.

## Children's Privacy
Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13.

## Links to Other Websites
Our Service may contain links to other websites. We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.

## Changes to this Privacy Policy
We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.

## Contact Us
If you have any questions about this Privacy Policy, You can contact us:
* By email: feedbackflashfonic@gmail.com
`;

// --- HELPER FUNCTIONS ---
// moved to ./utils/helpers.js

// --- MODAL AND UI COMPONENTS ---

// DocViewer extracted to components/DocViewer.jsx

// EULAModal extracted to components/EULAModal.jsx

// FlashFonicModeModal extracted to components/FlashFonicModeModal.jsx

// ImageSourceModal extracted to components/ImageSourceModal.jsx


// LandingPage extracted to components/LandingPage.jsx

// HowToPlayModal extracted to components/HowToPlayModal.jsx

// EnterNameModal extracted to components/EnterNameModal.jsx

// GamesModal extracted to components/GamesModal.jsx

// ExamConfigModal extracted to components/ExamConfigModal.jsx

// ExplanationModal extracted to components/ExplanationModal.jsx

// ProgressModal extracted to components/ProgressModal.jsx

// ExamHistoryModal extracted to components/ExamHistoryModal.jsx

// AnamnesisNemesisLandingPage extracted to components/AnamnesisNemesisLandingPage.jsx

// CreateFolderModal extracted to components/CreateFolderModal.jsx

// PromptModal extracted to components/PromptModal.jsx

// ConfirmModal extracted to components/ConfirmModal.jsx

// FeedbackModal extracted to components/FeedbackModal.jsx

// FlashNotesActionModal extracted to components/FlashNotesActionModal.jsx

// FlashNotesViewer extracted to components/FlashNotesViewer.jsx

// ActionsDropdown extracted to components/ActionsDropdown.jsx


// FlashcardViewer extracted to components/FlashcardViewer.jsx

// GameViewer extracted to components/GameViewer.jsx

// ExamViewer extracted to components/ExamViewer.jsx

// ChemicalImage extracted to components/ContentRenderer.jsx

// KatexRenderer extracted to components/ContentRenderer.jsx

// ContentRenderer extracted to components/ContentRenderer.jsx

// --- REFACTOR FIX: STABLE & CORRECTED COMPONENTS ---
// These components are now defined outside MainApp to prevent re-renders and fix all bugs.

// EditableCardContent extracted to components/EditableCardContent.jsx

// FolderItem extracted to components/FolderItem.jsx

// --- APP WRAPPER COMPONENT ---
const AppWrapper = ({ 
    isDevMode, 
    appReady, 
    setAppReady, 
    audioInitialized, 
    handleEULAAccept, 
    EULAModal, 
    eulaContent, 
    children,
    containerClassName
}) => {
    const location = useLocation();

    useEffect(() => {
        try {
            console.log('AppWrapper: useEffect running, isDevMode:', isDevMode);
            const hasAcceptedEULA = localStorage.getItem('flashfonic-eula-accepted') === 'true';
            console.log('AppWrapper: hasAcceptedEULA:', hasAcceptedEULA);
            
            if (isDevMode || hasAcceptedEULA) {
                // Do not block app readiness on audio policy; start audio in background
                console.log('AppWrapper: Setting appReady to true');
                setAppReady(true);
                try {
                    console.log('AppWrapper: Starting audio initialization (non-blocking)');
                    if (!audioInitialized.current) {
                        loadTone()
                            .then((Tone) => Tone.start())
                            .then(() => {
                                audioInitialized.current = true;
                                console.log('AppWrapper: Audio initialized successfully');
                            })
                            .catch((err) => {
                                console.warn(': Audio initialization failed:', err);
                            });
                    }
                } catch (err) {
                    console.warn(': Audio initialization threw synchronously:', err);
                }
            } else {
                console.log('AppWrapper: EULA not accepted, showing EULA modal');
            }
        } catch (error) {
            console.error('AppWrapper: Error in useEffect:', error);
            throw error;
        }
    }, [isDevMode, audioInitialized, setAppReady]);

    // Auto-accept on first visit — passive disclaimer shown in app footer
    if (!localStorage.getItem('flashfonic-eula-accepted')) {
        localStorage.setItem('flashfonic-eula-accepted', 'true');
    }
    if (!appReady) return null;

    return (
        <div className={`main-app-container${containerClassName ? ' ' + containerClassName : ''}`}>
            {children}
        </div>
    );
};

// --- Top-Level App Component ---
const App = () => {
    const [showDocViewer, setShowDocViewer] = useState(null);
    const location = useLocation();
    const [appReady, setAppReady] = useState(false);
    const audioInitialized = useRef(false);
    const [showPlanChooserTop, setShowPlanChooserTop] = useState(false);
    const [isClinicalsPlanChooser, setIsClinicalsPlanChooser] = useState(false);
    const [planChooserReason, setPlanChooserReason] = useState(null);
    const [showLogin, setShowLogin] = useState(false);
    const navigate = useNavigate();
    const [entitlements, setEntitlements] = useState(null);
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [usage, setUsage] = useState({ count: 0, limit: 25, date: '' });
    const [postLoginModal, setPostLoginModal] = useState(null); // 'plans' | 'billing' | 'landing'
    const [isGuestMode, setIsGuestMode] = useState(false);
    const [promoNudge, setPromoNudge] = useState({ visible: false, count: 0 });
    const [genPromo, setGenPromo] = useState({ visible: false, count: 0 });
    const [freeLimitModal, setFreeLimitModal] = useState(false);

    useEffect(() => {
        const onPromo = (e) => {
            try { setPromoNudge({ visible: true, count: Number(e?.detail?.count || 0) }); } catch(_) {}
        };
        const onLimit = () => setFreeLimitModal(true);
        window.addEventListener('ff:showPromoNudge', onPromo);
        window.addEventListener('ff:showFreeLimit', onLimit);
        return () => {
            window.removeEventListener('ff:showPromoNudge', onPromo);
            window.removeEventListener('ff:showFreeLimit', onLimit);
        };
    }, []);

    // Removed modal auto-open on /terms and /privacy to use standalone static pages

    const startSubscriptionCheckoutTop = async (planOrPrice) => {
        try {
            // Require non-anonymous auth before starting Stripe checkout
            try {
                const auth = getAuthInstance();
                if (!auth || !auth.currentUser || auth.currentUser.isAnonymous) {
                    setPostLoginModal('plans');
                    setShowLogin(true);
                    return;
                }
            } catch (_) {
                setPostLoginModal('plans');
                setShowLogin(true);
                return;
            }
            try { trackEvent('begin_checkout', { mode: 'subscription', plan: typeof planOrPrice === 'string' ? planOrPrice : (planOrPrice?.plan || 'unknown') }); } catch (_) {}
            try { trackPixels('InitiateCheckout', { content_name: typeof planOrPrice === 'string' ? planOrPrice : (planOrPrice?.plan || 'subscription') }); } catch (_) {}
            const data = await authApiIdempotent(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(
                    typeof planOrPrice === 'string' ? { plan: planOrPrice } : planOrPrice
                )
            });
            window.location.href = data.url;
        } catch (e) {
            console.error(e);
            alert(e.message);
        }
    };

    const openBillingPortalTop = async () => {
        try {
            const data = await authApiIdempotent(`${API_BASE_URL}/api/stripe/create-portal-session`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ returnUrl: window.location.origin + '/account' })
            });
            window.location.href = data.url;
        } catch (e) {
            console.error(e);
            alert(e.message);
        }
    };

    const startCreditPackCheckoutTop = async (packId) => {
        try {
            // Require non-anonymous auth before starting Stripe checkout for credit packs
            try {
                const auth = getAuthInstance();
                if (!auth || !auth.currentUser || auth.currentUser.isAnonymous) {
                    setPostLoginModal('billing');
                    setShowLogin(true);
                    return;
                }
            } catch (_) {
                setPostLoginModal('billing');
                setShowLogin(true);
                return;
            }
            try { trackEvent('begin_checkout', { mode: 'payment', packId }); } catch (_) {}
            try { trackPixels('InitiateCheckout', { content_name: `credits_${packId}` }); } catch (_) {}
            const data = await authApiIdempotent(`${API_BASE_URL}/api/stripe/create-credits-checkout`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ packId })
            });
            window.location.href = data.url;
        } catch (e) {
            console.error(e);
            alert(e.message);
        }
    };

    const ensureGuestSession = useCallback(async () => {
        try {
            const auth = getAuthInstance();
            // If the user has previously created an account, prefer waiting for restore
            let hasAccountHint = false;
            try { hasAccountHint = !!localStorage.getItem('ff:hasAccount'); } catch(_) {}
            if (auth && (!auth.currentUser)) {
                // First, wait for persisted session to rehydrate
                try { await waitForUserAvailable(3500); } catch (_) {}
            }
            if (auth && (!auth.currentUser)) {
                if (!hasAccountHint) {
                    try { await signInAnonymously(auth); } catch (_) {}
                    try { await waitForUserAvailable(2000); } catch (_) {}
                } else {
                    // As a final fallback if restore still failed after a grace period
                    try { await waitForUserAvailable(2000); } catch (_) {}
                }
            }
        } catch (_) {}
    }, []);

    const resetGuestUsage = useCallback(() => {
        const today = new Date().toISOString().split("T")[0];
        const fresh = { count: 0, limit: 25, date: today };
        setUsage(fresh);
        try { localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(fresh)); } catch (_) {}
    }, []);

    const refreshEntitlements = useCallback(async () => {
        let data = null;
        try {
            data = await authApi(`${API_BASE_URL}/api/me/entitlements`);
        } catch (_) {
            // swallow and fall back to Firestore
        }

        // Fallback: read creditsBalance directly from Firestore user doc if API not available or missing credits
        try {
            const auth = getAuthInstance();
            const db = await waitForDbAvailable(2000);
            const uid = auth && auth.currentUser && !auth.currentUser.isAnonymous ? auth.currentUser.uid : null;
            if (db && uid) {
                const snap = await getDoc(fsDoc(db, 'users', uid));
                if (snap && snap.exists()) {
                    const u = snap.data() || {};
                    const fbCredits = typeof u.creditsBalance === 'number' ? u.creditsBalance : undefined;
                    if (!data) data = {};
                    if (fbCredits !== undefined) data.creditsBalance = fbCredits;
                    if (!data.tier) data.tier = u.tier || 'free';
                    if (!data.uid) data.uid = uid;
                }
            } else if (db && auth && auth.currentUser && auth.currentUser.isAnonymous) {
                // Anonymous fallback: read credits from users/<anonUid>
                const anonUid = auth.currentUser.uid;
                const snap = await getDoc(fsDoc(db, 'users', anonUid));
                if (snap && snap.exists()) {
                    const u = snap.data() || {};
                    const fbCredits = typeof u.creditsBalance === 'number' ? u.creditsBalance : undefined;
                    if (!data) data = {};
                    if (fbCredits !== undefined) data.creditsBalance = fbCredits;
                    if (!data.tier) data.tier = u.tier || 'free';
                    if (!data.uid) data.uid = anonUid;
                }
            }
        } catch (_) {
            // ignore firestore fallback errors
        }

        if (data) {
            setEntitlements(data);
        }
        return data || null;
    }, []);

    useEffect(() => {
        let unsubscribe = null;
        (async () => {
            await initFirebase();
            
            // Check if user is returning from Stripe or payment flow
            const isReturningFromStripe = (() => {
                try {
                    const path = window.location.pathname;
                    const search = window.location.search;
                    return path === '/success' || search.includes('session_id=') || search.includes('stripe=return');
                } catch(_) { return false; }
            })();
            
            // Ensure a session is established, but wait longer if returning from Stripe
            try {
                const a = getAuthInstance();
                const hasAccountHint = (() => { try { return !!localStorage.getItem('ff:hasAccount'); } catch(_) { return false; } })();
                
                if (a && !a.currentUser) {
                    // Always give a persisted session (anonymous or real) a chance to restore
                    // from IndexedDB before deciding none exists — this used to only wait for
                    // accounts with a real-account hint (ff:hasAccount, only ever set on a
                    // real sign-in). Anonymous-only users skipped straight to "no user, sign
                    // in fresh," racing ahead of Firebase's async session restore on slower
                    // devices and silently minting a brand new anonymous identity that has no
                    // connection to the old one's data — unrecoverable, since anonymous
                    // sessions have no login path back.
                    const waitTime = isReturningFromStripe ? 5000 : 3500;
                    try { await waitForUserAvailable(waitTime); } catch (_) {}

                    // Only sign in anonymously if still no user and no account hint
                    if (a && !a.currentUser && !hasAccountHint && !isReturningFromStripe) {
                        try { await signInAnonymously(a); } catch (_) {}
                        try { await waitForUserAvailable(2000); } catch (_) {}
                    }
                }
            } catch (_) {}
            trackEvent(Events.app_open);
            try {
                const data = await refreshEntitlements();
                setIsGuestMode((data?.tier || 'free') === 'free');
            } catch (_) {}
            unsubscribe = onAuth(async (u) => {
                const data = await refreshEntitlements().catch(() => null);
                setIsGuestMode((data?.tier || 'free') === 'free');
                // If user became non-anonymous, ensure we're in the main UI
                try {
                    if (u && !u.isAnonymous && location.pathname === '/') {
                        navigate('/app/home', { replace: true });
                    }
                } catch (_) {}
            });
            try { await completeMagicLinkIfPresent(); } catch (_) {}
            // If returning from Stripe, poll entitlements briefly to absorb webhook lag
            try {
                const qs = new URLSearchParams(window.location.search);
                if (qs.get('stripe') === 'return') {
                    let tries = 0; const max = 10;
                    const poll = async () => {
                        tries++;
                        const ent = await refreshEntitlements().catch(() => null);
                        if (ent && (ent.tier !== 'free' || Number(ent.creditsBalance||0) > 25)) return;
                        if (tries < max) setTimeout(poll, 2000);
                    };
                    setTimeout(poll, 1200);
                }
            } catch(_) {}
        })();
        return () => {
            if (typeof unsubscribe === 'function') {
                try { unsubscribe(); } catch (_) {}
            }
        };
    }, [refreshEntitlements]);

    const handleStartLandingFlow = async () => {
        // Enter the app immediately for a snappy experience
        trackEvent(Events.landing_enter);
        // Set onboarding pending so AppHome wizard fires on first visit
        try {
            if (localStorage.getItem('ff:onboarding:completed') !== 'done') {
                localStorage.setItem('ff:onboarding:completed', 'pending');
            }
        } catch (_) {}
        navigate('/app/home');
        // Perform guest/bootstrap work in the background without blocking navigation
        (async () => {
            try {
                await ensureGuestSession();
                const ent = await refreshEntitlements().catch(() => null);
                if (ent) setIsGuestMode((ent.tier || 'free') === 'free');
                try { window.dispatchEvent(new CustomEvent('ff:enterGuest')); } catch (_) {}
            } catch (error) {
                console.warn('handleStartLandingFlow: continuing after guest bootstrap error', error);
            }
        })();
        // Do not show signup modal here; it will be shown after the first
        // free-trial flashcard generation from within AppHome.
    };

    const handleEULAAccept = async () => {
        const isDevMode = new URLSearchParams(window.location.search).get('dev') === 'true';
        if (!isDevMode) {
            localStorage.setItem('flashfonic-eula-accepted', 'true');
        }
        trackEvent(Events.eula_accepted);
        try {
            if (!audioInitialized.current) {
                const Tone = await loadTone();
                await Tone.start();
                console.log("AudioContext started after EULA acceptance.");
                audioInitialized.current = true;
            }
        } catch (err) {
            console.warn('Audio initialization failed after EULA acceptance, continuing.', err);
        }
        // Persist acceptance to Firestore via backend
        try {
            await authApi(`${API_BASE_URL}/api/me/accept-eula`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ version: 'v1' })
            });
        } catch (_) {}
        setAppReady(true);
    };

    const isDevMode = new URLSearchParams(window.location.search).get('dev') === 'true';

    // SPA pageview tracking (Vercel + GA4 + Pixels)
    useEffect(() => {
        if (DEAD_REDIRECT_PATHS.has(location.pathname)) return;
        try { track('pageview', { path: location.pathname }); } catch (_) {}
        // The browser tab title never changed per route (single static <title> in index.html),
        // so even a correct page_title would report the same value for every page. Set it here
        // before reading it for GA4, so "Pages and screens" can actually tell pages apart.
        try { document.title = getPageTitle(location.pathname); } catch (_) {}
        // GA4's own "Pages and screens" report parses page_location as a full URL and reads
        // page_title separately — sending just the bare pathname (no origin) with no title at
        // all is why so many rows in that report show up blank, undefined, or collapsed to "/".
        try {
            trackEvent('page_view', {
                page_location: window.location.href,
                page_path: location.pathname,
                page_title: document.title,
            });
        } catch (_) {}
        try { trackPixels('PageView', { path: location.pathname }); } catch (_) {}
    }, [location.pathname]);

    // Handle guest entry and open login modal events
    useEffect(() => {
        const onEnterGuest = async () => {
            try {
                await ensureGuestSession();
                await refreshEntitlements();
                setIsGuestMode(true);
            } catch (_) {}
        };
        const onOpenLogin = () => { try { setShowLogin(true); } catch(_) {} };
        window.addEventListener('ff:enterGuest', onEnterGuest);
        window.addEventListener('ff:openLogin', onOpenLogin);
        return () => {
            window.removeEventListener('ff:enterGuest', onEnterGuest);
            window.removeEventListener('ff:openLogin', onOpenLogin);
        };
    }, [ensureGuestSession, resetGuestUsage, refreshEntitlements]);

    return (
        <>
            <Analytics />
            <SpeedInsights />
            <OfflineBanner />
            {/* Only worth prompting install once someone's actually in the app with
                content to gain from it — not on the public marketing/landing pages. */}
            {location.pathname.startsWith('/app') && <InstallAppBanner />}
            <Routes>
                <Route path="/" element={<LandingPage onEnter={handleStartLandingFlow} onOpenPlanChooser={(opts) => { setIsClinicalsPlanChooser(!!opts?.isClinicals); setPlanChooserReason(opts?.reason || null); setShowPlanChooserTop(true); }} onOpenAccount={() => setShowLogin(true)} />} />
                <Route path="/app" element={<Navigate to="/app/home" replace />} />
                <Route path="/app/home" element={
                    <AppWrapper 
                        isDevMode={isDevMode}
                        appReady={appReady}
                        setAppReady={setAppReady}
                        audioInitialized={audioInitialized}
                        handleEULAAccept={handleEULAAccept}
                        EULAModal={EULAModal}
                        eulaContent={eulaContent}
                        showDocViewer={showDocViewer}
                        setShowDocViewer={setShowDocViewer}
                        navigate={navigate}
                        setShowLogin={setShowLogin}
                        containerClassName="home-wide"
                    >
                        <AppHome
                            isDevMode={isDevMode}
                            entitlements={entitlements}
                            refreshEntitlements={refreshEntitlements}
                            onOpenAccount={() => setShowLogin(true)}
                            onOpenPlanChooser={(opts) => { setIsClinicalsPlanChooser(!!opts?.isClinicals); setPlanChooserReason(opts?.reason || null); setShowPlanChooserTop(true); }}
                            onSelectFoto={() => navigate('/app/foto', { replace: true })}
                        />
                    </AppWrapper>
                } />
                <Route path="/app/deck/:folderId" element={
                    <AppWrapper
                        isDevMode={isDevMode}
                        appReady={appReady}
                        setAppReady={setAppReady}
                        audioInitialized={audioInitialized}
                        handleEULAAccept={handleEULAAccept}
                        EULAModal={EULAModal}
                        eulaContent={eulaContent}
                        navigate={navigate}
                        setShowLogin={setShowLogin}
                        containerClassName="home-wide"
                    >
                        <DeckPage
                            isDevMode={isDevMode}
                            entitlements={entitlements}
                            refreshEntitlements={refreshEntitlements}
                            onOpenAccount={() => setShowLogin(true)}
                            onOpenPlanChooser={() => setShowPlanChooserTop(true)}
                        />
                    </AppWrapper>
                } />
                <Route path="/clinicals" element={<FlashClinicalLandingPage entitlements={entitlements} onOpenAccount={() => setShowLogin(true)} onOpenPlanChooser={(opts) => { setIsClinicalsPlanChooser(!!opts?.isClinicals); setShowPlanChooserTop(true); }} />} />
                <Route path="/for/:pathwaySlug" element={<PathwayPage onEnter={handleStartLandingFlow} onOpenAccount={() => setShowLogin(true)} onOpenPlanChooser={(opts) => { setIsClinicalsPlanChooser(!!opts?.isClinicals); setShowPlanChooserTop(true); }} />} />
                <Route path="/s/:shareId" element={<PublicStudyPage />} />
                <Route path="/eula" element={<EulaPage handleEULAAccept={handleEULAAccept} eulaContent={eulaContent} />} />
                <Route path="/terms" element={<StaticDocPage title="Terms and Conditions" content={eulaContent} />} />
                <Route path="/privacy" element={<StaticDocPage title="Privacy Policy" content={privacyPolicyContent} />} />
                <Route path="/app/live" element={<Navigate to="/app/home?mode=fonic" replace />} />
                <Route path="/app/upload" element={<Navigate to="/app/home?mode=fonic" replace />} />
                <Route path="/app/study" element={<Navigate to="/app/home" replace />} />
                <Route path="/app/exams" element={<Navigate to="/app/home" replace />} />
                <Route path="/app/games" element={<Navigate to="/app/home" replace />} />
                <Route path="/app/notes" element={<Navigate to="/app/home" replace />} />
                <Route path="/app/folders" element={<Navigate to="/app/home" replace />} />
                <Route path="/pricing" element={<Navigate to="/" replace />} />
                <Route path="/account" element={<AccountPage entitlements={entitlements} />} />
                <Route path="/success" element={<CheckoutSuccess />} />
                <Route path="/cancel" element={<CheckoutCancel />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {showPlanChooserTop && (
                <PlanChooserModal
                    entitlements={entitlements}
                    isClinicals={isClinicalsPlanChooser}
                    reason={planChooserReason}
                    onClose={() => { setShowPlanChooserTop(false); setIsClinicalsPlanChooser(false); setPlanChooserReason(null); }}
                    onManage={() => { setShowPlanChooserTop(false); setIsClinicalsPlanChooser(false); setPlanChooserReason(null); openBillingPortalTop(); }}
                    onChoosePlan={(key) => { setShowPlanChooserTop(false); setIsClinicalsPlanChooser(false); setPlanChooserReason(null); startSubscriptionCheckoutTop(key); }}
                    onRequireLogin={() => { setPostLoginModal('plans'); setShowPlanChooserTop(false); setIsClinicalsPlanChooser(false); setPlanChooserReason(null); setShowLogin(true); }}
                    onBuyPack={(pack) => {
                        setShowPlanChooserTop(false);
                        setIsClinicalsPlanChooser(false);
                        setPlanChooserReason(null);
                        if (!entitlements || entitlements.tier === 'free') {
                            alert('Credit packs are available only to subscribers. Please choose a plan first.');
                            setShowPlanChooserTop(true);
                            return;
                        }
                        startCreditPackCheckoutTop(pack);
                    }}
                    showCreditPacks={!!entitlements && entitlements.tier !== 'free'}
                />
            )}
            {showLogin && (
                <LoginModal
                    onClose={() => {
                        setShowLogin(false);
                        // After closing login, reopen the modal the user intended
                        if (postLoginModal === 'plans') {
                            setPostLoginModal(null);
                            setShowPlanChooserTop(true);
                        } else if (postLoginModal === 'billing') {
                            setPostLoginModal(null);
                            setShowBillingModal(true);
                        } else if (postLoginModal === 'landing') {
                            setPostLoginModal(null);
                        }
                    }}
                    entitlements={entitlements}
                    onOpenManageBilling={() => setShowBillingModal(true)}
                    onOpenPlanChooser={() => { setShowLogin(false); setIsClinicalsPlanChooser(true); setShowPlanChooserTop(true); }}
                />
            )}
            {showBillingModal && (
                <BillingPortalModal
                    onClose={() => setShowBillingModal(false)}
                    onOpenPortal={() => { setShowBillingModal(false); openBillingPortalTop(); }}
                    onOpenPlans={() => { setShowBillingModal(false); setIsClinicalsPlanChooser(true); setShowPlanChooserTop(true); }}
                    hasUser={(() => { try { const a = getAuthInstance(); return !!(a && a.currentUser && !a.currentUser.isAnonymous); } catch(_) { return !!(entitlements && entitlements.uid && entitlements.uid !== 'anonymous'); } })()}
                    onRequireLogin={() => { setPostLoginModal('billing'); setShowBillingModal(false); setShowLogin(true); }}
                    onBuyPack={(pack) => {
                        setShowBillingModal(false);
                        if (!entitlements || entitlements.tier === 'free') {
                            alert('Credit packs are available only to subscribers. Please choose a plan first.');
                            setShowPlanChooserTop(true);
                            return;
                        }
                        startCreditPackCheckoutTop(pack);
                    }}
                    showCreditPacks={!!entitlements && entitlements.tier !== 'free'}
                />
            )}
            {/* Global promo funnel modals */}
            {promoNudge.visible && (
                <Modal title="Unlock 100 extra credits" onClose={() => setPromoNudge(p => ({ ...p, visible: false }))}>
                    <div className="space-y-3">
                        <p className="text-sm text-slate-300">Enjoying FlashFonic? Subscribe today to unlock an additional 100 free credits for your first month!</p>
                        <p className="text-[11px] text-slate-400">Free tier balance dropped below {promoNudge.count}.</p>
                        <div className="flex justify-end gap-2">
                            <button className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10" onClick={() => setPromoNudge(p => ({ ...p, visible: false }))}>Back</button>
                            <button className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-white bg-gradient-to-r from-teal-600 to-emerald-600" onClick={() => { setPromoNudge(p => ({ ...p, visible: false })); setShowPlanChooserTop(true); }}>Subscribe</button>
                        </div>
                    </div>
                </Modal>
            )}
            {genPromo.visible && (
                <Modal title="Claim 100 bonus credits" onClose={() => setGenPromo(p => ({ ...p, visible: false }))}>
                    <div className="space-y-3">
                        <p className="text-sm text-slate-300">Nice! You've generated {genPromo.count}+ flashcards. Claim 100 bonus credits now.</p>
                        {isClaimingGen && (
                            <p className="text-[11px] text-slate-400">Applying bonus to your account…</p>
                        )}
                        <div className="flex justify-end gap-2">
                            <button disabled={isClaimingGen} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm ${isClaimingGen ? 'text-slate-400' : 'text-slate-200'} bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10`} onClick={() => setGenPromo(p => ({ ...p, visible: false }))}>Back</button>
                            <button disabled={isClaimingGen} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-white bg-gradient-to-r from-teal-600 to-emerald-600 ${isClaimingGen ? 'opacity-70' : ''}`} onClick={async () => { try { setIsClaimingGen(true); await claimGenerationPromo(); } finally { setIsClaimingGen(false); setGenPromo(p => ({ ...p, visible: false })); } }}>
                                {isClaimingGen ? 'Claiming…' : 'Claim offer'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
            {freeLimitModal && (
                <Modal title="You've reached your free limit" onClose={() => setFreeLimitModal(false)}>
                    <div className="space-y-4">
                        <p className="text-sm text-slate-300">You've reached your 25 credit limit for this month. Upgrade your subscription to keep flashing!</p>
                        <div className="flex justify-end gap-2">
                            <button className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10" onClick={() => setFreeLimitModal(false)}>Back</button>
                            <button className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-white bg-gradient-to-r from-teal-600 to-emerald-600" onClick={() => { setFreeLimitModal(false); setShowPlanChooserTop(true); }}>Upgrade now</button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default App;
